import { Box } from '@wix/design-system';
import { RecommendationLabel } from '@wix/ambassador-premium-domains-v1-recommendation/types';
import { CoreDomainSearchStore, DomainSearchSuiteModule, SearchRecommendationModule } from '@wix/domains-search-suite';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import { SiteLevelDomainTaken } from '../../../Components/StatusesOverrides/SiteLevel';
import { BackButton } from '../../../Components/Headers/HeaderWrapper';
import { ResultsHeader } from '../../../Components/Headers/ResultsHeader/ResultsHeader';
import { Recommendations, RecommendationsError } from '../../../Components/Recommendations';
import { VoucherBanner } from '../../../Components/VoucherBanner';
import { useDomainSearchGateBi } from '../../../hooks/useDomainSearchGateBi';
import { useIsMailboxReferralBM } from '../../../hooks/useIsMailboxReferral';
import { useResultsPageConfig } from '../../../hooks/useResultsPageConfig';
import { useGatePageBackground } from '../../../hooks/useGatePageBackground';
import { useIsBmHarmonyThemeEnabled } from '../../../utils/harmonyTheme';
import { HarmonyThemeProvider } from '@wix/core-domain-search';
import { useQueryParams } from '../../../hooks/useQueryParams';
import { useQuerySubmit } from '../../../hooks/useQuerySubmit';
import { DEFAULT_LIMIT, useRecommendations } from '../../../hooks/useRecommendations';
import { useResultsBack } from '../../../hooks/useResultsBack';
import { useResultsPageOfferingsBi } from '../../../hooks/useResultsPageOfferingsBi';
import { useTldPrefill } from '../../../hooks/useTldPrefill';
import { DomainSearchGateModule } from '../../../module/domainSearchGate.module';
import { TldPrefillDescription } from '../../ResultsLayout/TldPrefillDescription';
import { VoucherRow } from '../../ResultsLayout/VoucherRow';
import { useResultsSearchBarV4Layouts, V4_CONTENT_MAX_WIDTH, V4_SEARCH_BAR_PLACEHOLDER_KEY } from '../../ResultsLayout/resultsSearchBarV4Layouts';
import s from '../../resultsPage.scss';
import transition from '../../viewTransitions.css';
const SCROLL_TARGET_DATA_HOOK = 'site-level-results-scroll-target';
const CONTENT_INLINE_PADDING = '48px';
const freeTextStatusesCustomComponents = {
  domainTakenInWix: SiteLevelDomainTaken,
  upgradeRequireConnect: SiteLevelDomainTaken,
  upgradeRequireTransferIn: SiteLevelDomainTaken
};
export const SiteLevelDomainsResultsPage = _ref => {
  let handlers = _ref.handlers,
    externalViewer = _ref.externalViewer;
  const _useQueryParams = useQueryParams(),
    getQueryParam = _useQueryParams.getQueryParam;
  const autoInvokeOverride = getQueryParam('invoke') === 'true' ? true : undefined;
  const isHarmonyTheme = useIsBmHarmonyThemeEnabled();
  const _useResultsPageConfig = useResultsPageConfig(),
    isV41 = _useResultsPageConfig.isV41;
  return /*#__PURE__*/React.createElement(DomainSearchSuiteModule.Provider, {
    translations: {
      Loading: /*#__PURE__*/React.createElement(React.Fragment, null)
    },
    actions: {
      siteInfo: {
        externalViewer: Boolean(externalViewer)
      },
      handlers,
      autoInvokeOverride
    },
    statusesCustomComponents: {
      customConfiguration: isV41 ? freeTextStatusesCustomComponents : {}
    }
  }, /*#__PURE__*/React.createElement(HarmonyThemeProvider, {
    enabled: isHarmonyTheme
  }, /*#__PURE__*/React.createElement(SiteLevelDomainsResultsPageContent, null)));
};
export const SiteLevelDomainsResultsPageContent = () => {
  DomainSearchGateModule.SDK.hooks.useDomainsSearchGateBM();
  const _DomainSearchGateModu = DomainSearchGateModule.internals.useTranslations(),
    t = _DomainSearchGateModu.t;
  const _useResultsPageConfig2 = useResultsPageConfig(),
    isV41 = _useResultsPageConfig2.isV41;
  const isMailboxReferral = useIsMailboxReferralBM();
  const pageBackground = useGatePageBackground();
  const v41Layouts = useResultsSearchBarV4Layouts();
  const handleBackClick = useResultsBack();
  const _useQueryParams2 = useQueryParams(),
    getQueryParam = _useQueryParams2.getQueryParam;
  const _useTldPrefill = useTldPrefill(),
    prefillTld = _useTldPrefill.tld;
  useDomainSearchGateBi({
    viewMode: 'Gate Result Page',
    prefilledTld: prefillTld,
    isEmptyState: Boolean(prefillTld) && !getQueryParam('q')
  });
  const _useRecommendations = useRecommendations({
      limit: DEFAULT_LIMIT,
      enableEarlyExactMatch: true
    }),
    recommendations = _useRecommendations.recommendations,
    isLoading = _useRecommendations.isLoading,
    isLeadingCardLoading = _useRecommendations.isLeadingCardLoading,
    query = _useRecommendations.query,
    hasMore = _useRecommendations.hasNextPage,
    fetchNextPage = _useRecommendations.fetchNextPage,
    isFetchingNextPage = _useRecommendations.isFetchingNextPage,
    error = _useRecommendations.error,
    biError = _useRecommendations.biError,
    biErrorSubtype = _useRecommendations.biErrorSubtype,
    onRetry = _useRecommendations.onRetry,
    isPricingLoading = _useRecommendations.isPricingLoading;
  const _DomainSearchSuiteMod = DomainSearchSuiteModule.SDK.hooks.useUrlRecovery(),
    updateUrl = _DomainSearchSuiteMod.updateUrl;
  useQuerySubmit({});
  useResultsPageOfferingsBi({
    recommendations,
    isLoading,
    biError,
    biErrorSubtype
  });
  useEffect(() => {
    // Reset store when component unmounts
    return () => {
      CoreDomainSearchStore.resetStore();
    };
  }, []);
  const emptyQueryStatus = !isLoading && recommendations.length > 0 && !query;
  const tldPrefillEmptyState = Boolean(prefillTld) && !query;
  const searchBar = /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.DomainSearchBar, {
    compact: true,
    scrollIntoView: {
      dataHook: SCROLL_TARGET_DATA_HOOK
    },
    showSearchIcon: isV41,
    showOptionsIfEmptyInput: false,
    autoFocus: false,
    hideClearButton: isV41,
    hideFilters: tldPrefillEmptyState || isV41,
    placeholder: isV41 ? t(V4_SEARCH_BAR_PLACEHOLDER_KEY) : undefined,
    onSubmit: filters => {
      updateUrl(filters);
    },
    onFiltersChange: (key, value) => {
      if (key === 'tlds') {
        DomainSearchSuiteModule.internals.bi.onSelectTld == null || DomainSearchSuiteModule.internals.bi.onSelectTld({
          tld: JSON.stringify(value)
        });
      } else {
        DomainSearchSuiteModule.internals.bi.onCTAClick == null || DomainSearchSuiteModule.internals.bi.onCTAClick({
          ctaClick: key,
          value: JSON.stringify(value)
        });
      }
    },
    Layouts: {
      ...(isV41 && v41Layouts),
      StatusComponent: () => {
        if (tldPrefillEmptyState && prefillTld) {
          return /*#__PURE__*/React.createElement(TldPrefillDescription, {
            tld: prefillTld
          });
        }
        return emptyQueryStatus ? /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.EmptyQueryStatus, null) : /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.Status, null);
      }
    }
  });
  return /*#__PURE__*/React.createElement(Box, {
    align: "center",
    display: "grid",
    className: clsx(s.resultsPage, s.noInsights),
    gap: isV41 ? 0 : 'SP6 0',
    height: "100%",
    backgroundColor: pageBackground,
    overflow: "auto"
  }, /*#__PURE__*/React.createElement(Box, {
    dataHook: SCROLL_TARGET_DATA_HOOK,
    gridArea: "header",
    height: 0,
    overflow: "hidden",
    alignSelf: "start",
    pointerEvents: "none"
  }), /*#__PURE__*/React.createElement(Box, {
    boxSizing: "border-box",
    align: "center",
    gridArea: "header",
    top: "0",
    position: "sticky",
    width: "100%",
    zIndex: 10,
    padding: isV41 ? '16px 0 32px' : 'SP5 48px',
    backgroundColor: pageBackground,
    direction: "vertical",
    gap: isV41 ? '16px' : 'SP6'
  }, isV41 && /*#__PURE__*/React.createElement(VoucherRow, {
    maxWidth: V4_CONTENT_MAX_WIDTH,
    start: /*#__PURE__*/React.createElement(BackButton, {
      onClick: handleBackClick
    }),
    marginBottom: "0"
  }, /*#__PURE__*/React.createElement(VoucherBanner, null)), !isV41 && /*#__PURE__*/React.createElement(VoucherBanner, null), !isV41 && /*#__PURE__*/React.createElement(ResultsHeader, {
    className: transition.header,
    onBackClick: handleBackClick,
    isMailboxReferral: isMailboxReferral,
    offsetTitle: tldPrefillEmptyState,
    topAligned: !!prefillTld
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      width: '100%',
      zIndex: 11,
      ...(isV41 && {
        padding: `0 ${CONTENT_INLINE_PADDING}`,
        boxSizing: 'border-box'
      })
    },
    className: transition.searchBar
  }, searchBar)), tldPrefillEmptyState ? null : error ? /*#__PURE__*/React.createElement(RecommendationsError, {
    error: error,
    onRetry: onRetry,
    gridColumn: "1 / -1"
  }) :
  /*#__PURE__*/
  // Two channels, because the hero and main cards are siblings of the list rather than
  // items in it: the prop reaches the rows, the provider reaches the cards.
  React.createElement(SearchRecommendationModule.Provider, null, /*#__PURE__*/React.createElement(Recommendations, {
    recommendations: recommendations,
    isLoading: isLoading,
    isLeadingCardLoading: isLeadingCardLoading,
    hasMore: hasMore,
    isPricingLoading: isPricingLoading,
    fetchNextPage: () => {
      DomainSearchSuiteModule.internals.bi.onCTAClick == null || DomainSearchSuiteModule.internals.bi.onCTAClick({
        ctaClick: 'show_more_results'
      });
      fetchNextPage();
    },
    isFetchingNextPage: isFetchingNextPage,
    gridArea: "content",
    className: clsx(s.centeredContent, isV41 && s.narrowContent),
    useCompactRecommendationsLayout: isV41,
    showHeroCard: isV41,
    promotedLabels: isV41 ? [RecommendationLabel.EXACT_MATCH, RecommendationLabel.GREAT_ALTERNATIVE] : undefined,
    titleOverflowStrategy: isV41 ? 'scale' : 'ellipsis',
    clickableRows: isV41,
    clickableLeadingCard: !isV41,
    suppressHarmonyCardStyling: isV41,
    desktopContentPadding: isV41 ? '24px' : undefined,
    desktopContentGap: isV41 ? '24px' : undefined,
    insightsPaddingTop: isV41 ? '24px' : undefined,
    showTrustBuilders: false,
    alternativesHeadingGap: isV41 ? '24px' : undefined
  })));
};
//# sourceMappingURL=SiteLevelDomainsResultsPage.js.map